/* eslint-disable @typescript-eslint/no-unused-expressions */
import { ArcElement, Chart as ChartJS, ChartOptions, Legend, Tooltip } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useInView } from 'react-intersection-observer';
import { useEffect, useState } from 'react';
import * as Styled from './styles'

type Props = {
  title?: string
  dataProps?: Record<string, number>
  theme?: any
};

ChartJS.register(ArcElement, Tooltip, Legend);

type DoughnutEntry = {
  label: string;
  value: number;
  children?: DoughnutEntry[];
};

export const DoughnutAnalysis = ({ title, theme, dataProps }: Props) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0,
  });
  const [hasRendered, setHasRendered] = useState(false);

  useEffect(() => {
    if (inView && !hasRendered) {
      setHasRendered(true);
    }
  }, [inView, hasRendered]);

  const entries = Object.entries(dataProps ?? {}).map(([label, value]) => ({ label, value }));

  const totalValue = entries.reduce((sum, item) => sum + item.value, 0);

  const sortedEntries = [...entries].sort((a, b) => b.value - a.value);
  const topEntries = sortedEntries.slice(0, 3);
  const remaining = sortedEntries.slice(3);
  const othersTotal = remaining.reduce((sum, item) => sum + item.value, 0);
  const finalEntries: DoughnutEntry[] =
    othersTotal > 0 ? [...topEntries, { label: 'Outros', value: othersTotal, children: remaining }] : topEntries;

  const baseBackgroundColors = ['#2F6BB2', '#A070E0', '#51B3D6', '#26496E'];
  const baseLabelColors = ["#F8F9FA"];

  const formatPercentage = (value: number) => {
    if (totalValue === 0) return '0%';
    const percent = (value / totalValue) * 100;
    const formatted = percent % 1 === 0 ? percent.toFixed(0) : percent.toFixed(1);
    return `${formatted}%`;
  };

  const labels = finalEntries.map(entry => entry.label);
  const values = finalEntries.map(entry => entry.value);
  const backgroundColors = finalEntries.map((_, index) => baseBackgroundColors[index % baseBackgroundColors.length]);
  const labelColors = finalEntries.map((_, index) => baseLabelColors[index % baseLabelColors.length]);

  const data = {
    labels,
    datasets: [
      {
        data: values,
        datalabels: {
          labels: {
            title: {
              color: labelColors,
              font: { size: 12, weight: "bold" },
            },
          },
          title: {
            color: "#F8F9FA",
          },
          formatter: (value: number) => {
            return formatPercentage(value);
          },
        },
        backgroundColor: backgroundColors,
        borderWidth: 0,
        color: backgroundColors,
      },
    ],
  };

  const options: ChartOptions<'doughnut'> = {
    cutout: '60%',
    plugins: {
      legend: {
        display: false, 
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const entry = finalEntries[context.dataIndex];
            if (!entry) return context.label || '';
            return `${formatPercentage(entry.value)} ${entry.label}`;
          },
          afterBody: (tooltipItems) => {
            const entry = finalEntries[tooltipItems?.[0]?.dataIndex ?? 0];
            if (!entry?.children?.length) return [];
            return entry.children.map(child => `${formatPercentage(child.value)} ${child.label}`);
          },
        },
      },
    },
  };

  const labelsWithValue = data.labels.map((label, index) => {
    const value = data.datasets[0].data[index] as number;
    return `${formatPercentage(value)} ${label}`;
  });
  const legendColors = backgroundColors;

  return (
    <Styled.FinancialAnalysisContainer ref={ref}>
      {(hasRendered || inView) && (
        <div className="graphic">
          <div className="graphic-content">
            <Doughnut
              className="doughnut"
              data={{ labels: labelsWithValue, datasets: data.datasets as any }}
              options={options}
              plugins={[ChartDataLabels]}
            />

            <div className="custom-legend">
              {labelsWithValue.map((label, index) => (
                <div key={index} className="legend-item">
                  <span
                    className="legend-color-box"
                    style={{
                      backgroundColor: legendColors[index] ?? '#D4DEED',
                    }}
                  />
                  <span className="legend-text">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Styled.FinancialAnalysisContainer>
  );
};

